import * as React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {unifiedAccountApi} from '>root/apis';
import {useAsyncDispatch} from '>root/store/main';
import {UserUpdateActionParams, updateUser} from '>root/store/actions/account';
import {ActionType, Modal} from '>shared/components/modal/modal';
import {useModal} from '>shared/components/modal/useModal';
import {ModalDisplayType} from '>shared/components/modal/displayType';
import {
  FormBuilder,
  FormBuilderFields,
  FormBuilderFieldType,
  useAsyncSubmit,
} from '>shared/components/form/formBuilder';
import {Input} from '>shared/components/form/input';
import {CheckBox} from '>shared/components/form/checkBox';
import {UserRole} from '>generated/dvp.types';
import {ControlledSelect} from '>shared/components/form/select';
import {Option} from '>shared/components/form/rawControls/rawSelect';
import {useSnackbar} from '>shared/components/snackbars/useSnackbar';
import {BannerType, InfoBanner} from '>shared/components/banners/infoBanner';
import {WndError} from '>root/errors';

interface EditUserModalProps {
  accountId: string;
  authUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  roleLoggedInUser: UserRole;
  authUserIdLoggedInUser: string;
  initialRoleEditedUser: UserRole;
  roleOptions: Option[];
}

interface UpdateUser {
  role: UserRole;
  canManageBilling: boolean;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  accountId,
  authUserId,
  authUserIdLoggedInUser,
  firstName,
  lastName,
  email,
  roleLoggedInUser,
  initialRoleEditedUser,
  roleOptions,
}) => {
  const {hideModal} = useModal();
  const [isSaving, setIsSaving] = React.useState(false);
  const {showSuccessAlert, showErrorAlert} = useSnackbar();
  const asyncDispatch = useAsyncDispatch();

  const formMethods = useForm<UpdateUser>({
    mode: 'all',
    defaultValues: {
      role: initialRoleEditedUser,
    },
  });

  function canChangeCanManageBilling(currentlySetRole: UserRole): boolean {
    if (roleLoggedInUser !== UserRole.Owner) {
      return false;
    }

    return currentlySetRole === UserRole.AccountAdmin;
  }

  const [canManageBilling, setCanManageBilling] = React.useState(false);
  const [isManageBillingEnabled, setIsManageBillingEnabled] = React.useState(
    canChangeCanManageBilling(initialRoleEditedUser)
  );

  const canChangeRole = React.useCallback((): boolean => {
    if (roleLoggedInUser !== UserRole.Owner) {
      return false;
    }

    return initialRoleEditedUser === UserRole.AccountAdmin;
  }, [initialRoleEditedUser, roleLoggedInUser]);

  const onRoleChange = React.useCallback(
    (newRole: UserRole) => {
      if (newRole === UserRole.Owner) {
        setCanManageBilling(true);
        setIsManageBillingEnabled(false);
      } else {
        setIsManageBillingEnabled(canChangeCanManageBilling(newRole));
      }
    },
    [formMethods]
  );

  function isTransferringOwnership() {
    return (
      roleLoggedInUser === UserRole.Owner &&
      authUserId !== authUserIdLoggedInUser &&
      formMethods.getValues().role === UserRole.Owner
    );
  }

  React.useEffect(() => {
    unifiedAccountApi.getProductUserByAuthUserId({authUserId}).then(({data: productUser}) => {
      Object.keys(productUser.unifiedAccounts).find((unifiedAccountId) => {
        setCanManageBilling(
          Boolean(productUser.unifiedAccounts[unifiedAccountId].permissions.canManageBilling)
        );
      });
    });
  }, [authUserId]);

  const submitForm = useAsyncSubmit(
    formMethods,
    async (data, _event, _setFormError) => {
      setIsSaving(true);

      const {role} = data;

      const userUpdateParams: UserUpdateActionParams = {
        accountId,
        authUserId,
        permissions: {canManageBilling},
      };

      const willRoleChange = role !== initialRoleEditedUser;

      // Only pass to the API if it changed
      if (willRoleChange) {
        userUpdateParams.role = role;
      }

      try {
        await asyncDispatch(updateUser(userUpdateParams));

        setIsSaving(false);

        if (willRoleChange && role === UserRole.Owner) {
          showSuccessAlert(`${firstName} ${lastName} has successfully been assigned owner`);
        }
      } catch (err) {
        const error = err as WndError;

        setIsSaving(false);

        if (error.statusCode === 409) {
          showErrorAlert(`${email} already owns a Develop account and cannot own another.`);
        } else {
          throw err;
        }
      }

      hideModal();
    },
    [canManageBilling, initialRoleEditedUser]
  );

  const userInformationFormFields: FormBuilderFields = [
    {
      type: FormBuilderFieldType.Input,
      label: 'First Name',
      name: 'firstName',
      component: <Input value={firstName} disabled />,
    },
    {
      type: FormBuilderFieldType.Input,
      label: 'Last Name',
      name: 'lastName',
      component: <Input value={lastName} disabled />,
    },
    {
      type: FormBuilderFieldType.Input,
      label: 'Email',
      name: 'email',
      component: <Input value={email} disabled />,
    },
    {
      type: FormBuilderFieldType.Input,
      label: 'Role',
      name: 'role',
      component: (
        <ControlledSelect
          rules={{required: 'Role is required'}}
          options={roleOptions}
          onSelectChange={onRoleChange}
          disabled={!canChangeRole()}
          required
        />
      ),
    },
  ];

  const userPermissionFormFields: FormBuilderFields = [
    {
      type: FormBuilderFieldType.Custom,
      name: 'billingAccess',
      component: (
        <CheckBox
          label="Allow billing access"
          checked={canManageBilling}
          onChange={() => {
            if (formMethods.getValues().role === UserRole.Owner) {
              setCanManageBilling(true);
            } else {
              setCanManageBilling(!canManageBilling);
            }
          }}
          disabled={!isManageBillingEnabled}
        />
      ),
    },
  ];

  return (
    <FormProvider {...formMethods}>
      <Modal
        headerOptions={{title: 'Edit User'}}
        displayType={ModalDisplayType.Small}
        actions={[
          {
            type: ActionType.Primary,
            label: 'Save',
            onClick: submitForm,
            inputType: 'submit',
            qaDataAttribute: 'save-edit-user-button',
          },
          {
            type: ActionType.Secondary,
            label: 'Cancel',
            onClick: hideModal,
            qaDataAttribute: 'cancel-edit-user-button',
          },
        ]}
        isLocked={isSaving}
      >
        <div>
          <FormBuilder
            fields={[
              {
                type: FormBuilderFieldType.Group,
                fields: userInformationFormFields,
                label: 'User Information',
              },
              {
                type: FormBuilderFieldType.Group,
                fields: userPermissionFormFields,
                label: 'Permission Settings',
              },
            ]}
          />
          {isTransferringOwnership() && (
            <InfoBanner
              bannerType={BannerType.Warning}
              message={'Transferring account ownership will remove these permissions from you'}
            />
          )}
        </div>
      </Modal>
    </FormProvider>
  );
};
